import React, { useEffect, useState, ReactNode } from "react";
import { useSearchParams } from "react-router-dom";

import { TableButton } from "Components/UI/DataTable";
import {
  FilterOption,
  FilterOptionList,
  FilterPanelFooter,
  FilterPanelMessage,
  FilterPanelSearch,
  FilterPopover,
  FilterTrigger,
} from "Components/UI/TableFilters/TableFilters";
import { changeToParams } from "Shared/Utils/search-params";
import { useDebounce } from "Shared/Hook/use-debounce";

type DefaultOptionType = {
  id: string;
  name: string;
  [label: string]: string;
};

type ModalOptionsProps<T = DefaultOptionType> = {
  title: string;
  type: string;
  options: T[];
  getOptionValue?: (option: T) => string;
  getOptionLabel?: (option: T) => string;
  fetchOptions: (search: string) => void;
  /** Custom trigger; clicking it opens the panel. */
  node?: ReactNode;
  label?: string;
  disabled?: boolean;
  /** Kept for existing callers; the panel is portalled so stacking no longer matters. */
  zIndex?: boolean;
};

/** Single-select filter chip bound to the `type` URL param. */
function ModalOptions<T = DefaultOptionType>({
  title,
  type,
  options = [],
  label,
  fetchOptions,
  getOptionLabel = (option) =>
    label
      ? (option as DefaultOptionType)?.[label]
      : (option as DefaultOptionType)?.name,
  getOptionValue = (option) => (option as DefaultOptionType)?.id,
  node,
  disabled = false,
}: ModalOptionsProps<T>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [currentText, setCurrentText] = useState("");
  const [draft, setDraft] = useState({ value: "", label: "" });
  const debouncedCurrentText = useDebounce(currentText, 500);
  const applied = searchParams.get(type) || "";
  // The chosen label is remembered per param so the chip can name it after a reload.
  const storedLabel = localStorage.getItem(type) || "";

  useEffect(() => {
    fetchOptions(debouncedCurrentText);
  }, [debouncedCurrentText]);

  useEffect(() => {
    if (!searchParams.has(type)) localStorage.removeItem(type);
  }, [searchParams]);

  const labelOf = (value: string) => {
    const match = options.find((option) => getOptionValue(option) === value);
    return match ? getOptionLabel(match) : "";
  };

  const open = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setDraft({ value: applied, label: labelOf(applied) || storedLabel });
    setAnchor(event.currentTarget);
  };

  const close = () => {
    setAnchor(null);
    setCurrentText("");
  };

  const commit = (value: string, optionLabel: string) => {
    searchParams.delete("page");
    setSearchParams(changeToParams(type, value, searchParams));
    if (value) localStorage.setItem(type, optionLabel);
    else localStorage.removeItem(type);
    close();
  };

  const appliedLabel = applied ? labelOf(applied) || storedLabel || "1 selected" : "";

  return (
    <>
      {node ? (
        <span onClick={open}>{node}</span>
      ) : (
        <FilterTrigger
          label={title}
          value={appliedLabel}
          active={!!applied}
          open={!!anchor}
          disabled={disabled}
          title={disabled ? "Choose a form to select items." : undefined}
          onClick={open}
          onClear={() => commit("", "")}
        />
      )}
      <FilterPopover anchorEl={anchor} onClose={close} label={`Filter ${title}`}>
        <FilterPanelSearch
          value={currentText}
          onValueChange={setCurrentText}
          placeholder={`Search ${title.toLowerCase()}`}
        />
        <FilterOptionList>
          {options.length ? (
            options.map((option) => {
              const value = getOptionValue(option);
              return (
                <FilterOption
                  key={value}
                  selected={draft.value === value}
                  onClick={() => setDraft({ value, label: getOptionLabel(option) })}
                >
                  {getOptionLabel(option)}
                </FilterOption>
              );
            })
          ) : (
            <FilterPanelMessage>No matching options.</FilterPanelMessage>
          )}
        </FilterOptionList>
        <FilterPanelFooter
          start={
            <TableButton onClick={() => commit("", "")} disabled={!applied && !draft.value}>
              Clear
            </TableButton>
          }
        >
          <TableButton variant="outline" onClick={close}>
            Cancel
          </TableButton>
          <TableButton variant="primary" onClick={() => commit(draft.value, draft.label)}>
            Apply
          </TableButton>
        </FilterPanelFooter>
      </FilterPopover>
    </>
  );
}

export default ModalOptions;
